/**
 * Shared HTTP 429 helpers for the web app (Trello 430 RATE LIMIT, WP-13).
 *
 * Plain CommonJS and Node 12 syntax on purpose: server.js (plain node, no
 * babel, production runtime Node 12.22) requires this file for its own login
 * limiter, and the Next API proxies, the axios helpers and the React
 * components import the very same functions so every layer talks about a 429
 * the same way.
 *
 * What lives here
 *  - Retry-After parsing (delta-seconds or HTTP-date, or the `retry_after`
 *    member of the JSON envelope the new backend limiters add).
 *  - The user-facing copy (WP-20 salinan, id + en) and the choice of language.
 *  - A bounded, jittered backoff calculator for pollers.
 */

// Hard ceiling on any wait we are willing to show or sleep for: a hostile or
// broken upstream must not be able to park a visitor for hours.
const MAX_RETRY_AFTER_SECONDS = 3600;

// Seconds below which the copy counts "detik"; above it counts "menit" (the
// web login limiter answers with its whole 15 minute window, "900 detik" is
// accurate but unreadable).
const SECONDS_COPY_LIMIT = 120;

const COPY = {
  id: {
    generic: "Terlalu cepat. Tunggu {n} detik lalu coba lagi.",
    genericMinutes: "Terlalu cepat. Tunggu {n} menit lalu coba lagi.",
    genericNoWait: "Terlalu banyak permintaan. Coba lagi sebentar lagi.",
    login: "Terlalu banyak percobaan masuk. Coba lagi dalam {n} detik.",
    loginMinutes: "Terlalu banyak percobaan masuk. Coba lagi dalam {n} menit.",
    loginNoWait: "Terlalu banyak percobaan masuk. Coba lagi sebentar lagi.",
  },
  en: {
    generic: "You're going too fast. Please wait {n} seconds and try again.",
    genericMinutes: "You're going too fast. Please wait {n} minutes and try again.",
    genericNoWait: "Too many requests. Please try again in a moment.",
    login: "Too many sign-in attempts. Try again in {n}s.",
    loginMinutes: "Too many sign-in attempts. Try again in {n} minutes.",
    loginNoWait: "Too many sign-in attempts. Please try again in a moment.",
  },
};

const lowerCaseKeys = (headers) => {
  const out = {};
  if (!headers || typeof headers !== "object") return out;
  Object.keys(headers).forEach((key) => {
    out[String(key).toLowerCase()] = headers[key];
  });
  return out;
};

const readHeader = (headers, name) => {
  if (!headers) return undefined;
  if (typeof headers.get === "function") {
    try {
      const value = headers.get(name);
      if (value !== null && value !== undefined) return value;
    } catch (e) {
      // fall through to the plain-object lookup
    }
  }
  const value = lowerCaseKeys(headers)[name.toLowerCase()];
  return Array.isArray(value) ? value[0] : value;
};

/**
 * Retry-After value -> whole seconds (>= 0), or null when absent/unusable.
 * Accepts delta-seconds ("30"), a number, or an HTTP-date.
 */
const parseRetryAfterSeconds = (value, nowMs) => {
  if (value === undefined || value === null || value === "") return null;
  const now = typeof nowMs === "number" ? nowMs : Date.now();
  if (typeof value === "number") {
    if (!isFinite(value) || value < 0) return null;
    return Math.min(MAX_RETRY_AFTER_SECONDS, Math.ceil(value));
  }
  const text = String(value).trim();
  if (/^\d+(\.\d+)?$/.test(text)) {
    return Math.min(MAX_RETRY_AFTER_SECONDS, Math.ceil(Number(text)));
  }
  const dateMs = Date.parse(text);
  if (!isNaN(dateMs)) {
    return Math.min(MAX_RETRY_AFTER_SECONDS, Math.max(0, Math.ceil((dateMs - now) / 1000)));
  }
  return null;
};

const statusOf = (err) => {
  if (!err || typeof err !== "object") return NaN;
  const direct = Number(err.status || err.statusCode);
  if (direct) return direct;
  return Number(err.response && err.response.status);
};

/** True when `err` is the rejection of an HTTP 429 (axios response object or axios error). */
const isRateLimitError = (err) => statusOf(err) === 429;

/**
 * Seconds the server asked us to wait, from (in order) the Retry-After header
 * and the `retry_after` member of the body. null when neither is usable.
 */
const getRetryAfterSeconds = (err, nowMs) => {
  if (!err || typeof err !== "object") return null;
  const headers = err.headers || (err.response && err.response.headers);
  const fromHeader = parseRetryAfterSeconds(readHeader(headers, "retry-after"), nowMs);
  if (fromHeader !== null) return fromHeader;
  const data = err.data !== undefined ? err.data : (err.response && err.response.data);
  if (data && typeof data === "object") {
    const fromBody = parseRetryAfterSeconds(
      data.retry_after !== undefined ? data.retry_after : (data.data && data.data.retry_after),
      nowMs
    );
    if (fromBody !== null) return fromBody;
  }
  return null;
};

const normalizeLanguage = (value) => {
  if (!value) return null;
  const code = String(Array.isArray(value) ? value[0] : value).split("-")[0].toLowerCase();
  return code === "id" || code === "en" ? code : null;
};

/**
 * Language for a message produced outside React (API proxies). The site's
 * language cookie wins, then an explicit language field, then the visitor's
 * country (Indonesia -> id), else English (the site default).
 */
const resolveRequestLanguage = (req) => {
  const headers = (req && req.headers) || {};
  const cookieHeader = typeof headers.cookie === "string" ? headers.cookie : "";
  const cookieMatch = cookieHeader.match(/(?:^|;\s*)next-i18next=([^;]+)/);
  let cookieValue = cookieMatch ? cookieMatch[1] : null;
  if (cookieValue) {
    try {
      cookieValue = decodeURIComponent(cookieValue);
    } catch (e) {
      // a malformed %-sequence must never break an error response
    }
  }
  const fromCookie = normalizeLanguage(cookieValue);
  if (fromCookie) return fromCookie;
  const query = (req && req.query) || {};
  const body = (req && req.body && typeof req.body === "object") ? req.body : {};
  const explicit = normalizeLanguage(
    query.language || query.language_code || body.language || body.language_code
  );
  if (explicit) return explicit;
  const country = String(headers["cf-ipcountry"] || headers["x-http-ipcountry"] || "").toUpperCase();
  return country === "ID" ? "id" : "en";
};

/**
 * The friendly sentence for a 429.
 * @param {"generic"|"login"} kind
 * @param {number|null} seconds Retry-After in seconds (null = unknown)
 * @param {"id"|"en"} language
 */
const rateLimitMessage = (kind, seconds, language) => {
  const lang = normalizeLanguage(language) || "en";
  const copy = COPY[lang];
  const prefix = kind === "login" ? "login" : "generic";
  if (seconds === null || seconds === undefined || !isFinite(seconds)) {
    return copy[`${prefix}NoWait`];
  }
  const wait = Math.max(1, Math.ceil(seconds));
  if (wait >= SECONDS_COPY_LIMIT) {
    return copy[`${prefix}Minutes`].replace("{n}", String(Math.ceil(wait / 60)));
  }
  return copy[prefix].replace("{n}", String(wait));
};

const GENERIC_THROTTLE_TEXT = /too many|throttl|rate.?limit|slow down/i;

/**
 * Body for a 429 answered by a Next API proxy. JSON bodies keep every member
 * (clients classify OTP/limit codes from `data.code`, `meta.code`...), only a
 * missing or generic throttle `message` is replaced by the friendly sentence.
 * A plain-text or empty body becomes `{ message, retry_after }`.
 */
const buildRateLimitBody = (upstreamBody, seconds, language, kind) => {
  const message = rateLimitMessage(kind || "generic", seconds, language);
  const isObject = upstreamBody && typeof upstreamBody === "object" && !Array.isArray(upstreamBody);
  const body = isObject ? Object.assign({}, upstreamBody) : {};
  const existing = typeof body.message === "string" ? body.message : "";
  if (!existing || GENERIC_THROTTLE_TEXT.test(existing)) {
    body.message = message;
  }
  if (seconds !== null && seconds !== undefined && body.retry_after === undefined) {
    body.retry_after = seconds;
  }
  return body;
};

/**
 * Next delay (ms) for a poller after a 429.
 *  - never shorter than what the server asked for (Retry-After), capped;
 *  - grows exponentially across consecutive 429s;
 *  - plus a little jitter so a crowd that was throttled together does not wake
 *    together.
 */
const computeBackoffMs = (options) => {
  const opts = options || {};
  const baseMs = opts.baseMs > 0 ? opts.baseMs : 5000;
  const maxMs = opts.maxMs > 0 ? opts.maxMs : 60000;
  const jitterMs = opts.jitterMs >= 0 ? opts.jitterMs : 1000;
  const random = typeof opts.random === "function" ? opts.random : Math.random;
  const consecutive = Math.max(1, Number(opts.consecutive) || 1);
  const exponential = Math.min(maxMs, baseMs * Math.pow(2, Math.min(consecutive - 1, 10)));
  const hinted = opts.retryAfterSeconds > 0
    ? Math.min(maxMs, opts.retryAfterSeconds * 1000)
    : 0;
  return Math.min(maxMs, Math.max(exponential, hinted)) + Math.floor(random() * (jitterMs + 1));
};

/**
 * The sentence the sign-in forms show for a 429 (WP-20 wording), or null when
 * `err` is not a rate-limit rejection so the caller keeps its existing path.
 */
const loginRateLimitMessage = (err, language) => (
  isRateLimitError(err)
    ? rateLimitMessage("login", getRetryAfterSeconds(err), language)
    : null
);

const isPlainObject = (value) => !!value && typeof value === "object" && !Array.isArray(value);

/**
 * Language of whoever triggered an axios call: the server render's request
 * (ctx.req) or, in the browser, the site's own language cookie. null when the
 * caller is unknown (a Next API proxy calling the backend has neither; the
 * proxy writes its own message from the incoming request).
 */
const resolveCallerLanguage = (ctx) => {
  if (ctx && ctx.req) return resolveRequestLanguage(ctx.req);
  if (typeof document !== "undefined" && typeof document.cookie === "string") {
    return resolveRequestLanguage({ headers: { cookie: document.cookie } });
  }
  return null;
};

/**
 * Called from the axios helpers (helpers/axios.js, helpers/axiosInternal.js)
 * with the response of a failed call. Only a 429 is touched:
 *  - `retryAfter` (seconds, null when the server gave no usable hint) and,
 *    when the caller's language is known, `rateLimitMessage` (the friendly
 *    sentence) are added to the rejection, so UI code never has to parse
 *    headers;
 *  - an object body keeps every member; only a missing/generic `message` is
 *    replaced (same rule as the proxies);
 *  - a string/HTML body is left alone.
 * Returns the same object it was given.
 */
const annotateRateLimitResponse = (response, ctx) => {
  if (!isRateLimitError(response)) return response;
  const seconds = getRetryAfterSeconds(response);
  const language = resolveCallerLanguage(ctx);
  response.retryAfter = seconds;
  if (!language) return response;
  response.rateLimitMessage = rateLimitMessage("generic", seconds, language);
  const data = response.data;
  if (data === undefined || data === null || data === "" || isPlainObject(data)) {
    response.data = buildRateLimitBody(data, seconds, language, "generic");
  }
  return response;
};

module.exports = {
  loginRateLimitMessage,
  annotateRateLimitResponse,
  resolveCallerLanguage,
  MAX_RETRY_AFTER_SECONDS,
  SECONDS_COPY_LIMIT,
  COPY,
  parseRetryAfterSeconds,
  isRateLimitError,
  getRetryAfterSeconds,
  normalizeLanguage,
  resolveRequestLanguage,
  rateLimitMessage,
  buildRateLimitBody,
  computeBackoffMs,
};
